<template>
  <a-modal
      v-model:visible="visible"
      title="项目服务单详情"
      @cancel="handleCancel"
      width="1200px"
      :footer="false"
      draggable
  >
    <div class="service-order-info-modal-content">
      <div v-if="serviceOrderData" class="service-order-info">
        <!-- 基本信息 -->
        <a-card title="基本信息" style="margin-bottom: 16px;">
          <a-descriptions :column="3" layout="horizontal" bordered>
            <a-descriptions-item label="项目任务单">
              {{ getProjectTaskText(serviceOrderData.projectTaskId) }}
            </a-descriptions-item>
            <a-descriptions-item label="客户">
              {{ getCustomerText(serviceOrderData.customerCode) }}
            </a-descriptions-item>
            <a-descriptions-item label="门店">
              {{ getStoreText(serviceOrderData.storeCode) }}
            </a-descriptions-item>
            <a-descriptions-item label="服务类型">
              {{ getServiceTypeText(serviceOrderData.serviceType) || '—' }}
            </a-descriptions-item>
            <a-descriptions-item label="任务单类型">
              {{ getTaskCategoryText(serviceOrderData.taskCategory) || '—' }}
            </a-descriptions-item>
            <a-descriptions-item label="服务频次">
              {{ getServiceFrequencyText(serviceOrderData.serviceFrequency) || '—' }}
            </a-descriptions-item>
            <a-descriptions-item label="建议时间">
              {{ getSuggestedTimeText(serviceOrderData.suggestedTime, serviceOrderData.serviceFrequency) || '—' }}
            </a-descriptions-item>
            <a-descriptions-item label="服务内容" :span="3">
              {{ serviceOrderData.serviceContent || '—' }}
            </a-descriptions-item>
          </a-descriptions>
        </a-card>

        <!-- 服务信息 -->
        <a-card title="服务信息" style="margin-bottom: 16px;">
          <a-descriptions :column="3" layout="horizontal" bordered>
            <a-descriptions-item label="总需服务次数">
              {{ serviceOrderData.totalServiceCount || '—' }}
            </a-descriptions-item>
            <a-descriptions-item label="已完成服务次数">
              {{ serviceOrderData.completedServiceCount || '—' }}
            </a-descriptions-item>
            <a-descriptions-item label="服务人员">
              {{ getServiceStaffText(serviceOrderData.serviceStaffCode) }}
            </a-descriptions-item>
            <a-descriptions-item label="服务开始时间">
              {{ formatDate(serviceOrderData.serviceStartTime) }}
            </a-descriptions-item>
            <a-descriptions-item label="服务结束时间">
              {{ formatDate(serviceOrderData.serviceEndTime) }}
            </a-descriptions-item>
            <a-descriptions-item label="服务状态">
              <a-tag :color="getServiceStatusColor(serviceOrderData.serviceStatus)">
                {{ getServiceStatusText(serviceOrderData.serviceStatus) }}
              </a-tag>
            </a-descriptions-item>
          </a-descriptions>
        </a-card>

        <!-- 费用信息 -->
        <a-card title="费用信息" style="margin-bottom: 16px;">
          <a-descriptions :column="3" layout="horizontal" bordered>
            <a-descriptions-item label="单次金额">
              ¥{{ serviceOrderData.unitPrice ? serviceOrderData.unitPrice.toFixed(2) : '0.00' }}
            </a-descriptions-item>
            <a-descriptions-item label="单次服务人时">
              {{ serviceOrderData.unitServiceHours || '—' }}小时
            </a-descriptions-item>
            <a-descriptions-item label="是否强制协同">
              {{ getIsOvertimeText(serviceOrderData.isOvertime) }}
            </a-descriptions-item>
          </a-descriptions>
        </a-card>

        <!-- 其他信息 -->
        <a-card title="其他信息" style="margin-bottom: 16px;">
          <a-descriptions :column="2" layout="horizontal" bordered>
            <a-descriptions-item label="创建时间">
              {{ formatDate(serviceOrderData.createTime) }}
            </a-descriptions-item>
            <a-descriptions-item label="最后修改时间">
              {{ formatDate(serviceOrderData.updateTime) }}
            </a-descriptions-item>
            <a-descriptions-item label="备注" :span="2">
              {{ serviceOrderData.remark || '—' }}
            </a-descriptions-item>
            <a-descriptions-item label="扩展字段" :span="2">
              <pre v-if="serviceOrderData.extensionFields" style="white-space: pre-wrap; margin: 0;">{{ formatExtensionFields(serviceOrderData.extensionFields) }}</pre>
              <span v-else>—</span>
            </a-descriptions-item>
          </a-descriptions>
        </a-card>

        <!-- 异常记录 -->
        <a-card v-if="serviceOrderData.projectServiceExceptionList && serviceOrderData.projectServiceExceptionList.length > 0" title="异常记录">
          <a-table
              row-key="id"
              :columns="exceptionTableColumns"
              :data="serviceOrderData.projectServiceExceptionList"
              :pagination="false"
              stripe
              column-resizable
              :bordered="{cell:true}"
              size="small"
          >
            <template #exceptionStatus="{ record }">
              <!-- TODO 待替换数据字典 -->
              <a-tag :color="getExceptionStatusColor(record.exceptionStatus)">
                {{ getExceptionStatusText(record.exceptionStatus) }}
              </a-tag>
            </template>
            
            <template #exceptionStartTime="{ record }">
              {{ formatDate(record.exceptionStartTime) }}
            </template>

            <template #exceptionRecoveryTime="{ record }">
              {{ formatDate(record.exceptionRecoveryTime) }}
            </template>
          </a-table>
        </a-card>
      </div>

      <!-- 加载状态 -->
      <div v-else-if="loading" class="loading-container">
        <a-spin />
        <p>正在加载服务单详情...</p>
      </div>

      <!-- 无数据状态 -->
      <div v-else class="no-data-container">
        <a-empty description="暂无数据" />
      </div>
    </div>
  </a-modal>
</template>

<script setup>
import { ref, watch } from 'vue'
import { Message } from "@arco-design/web-vue"
import { getProjectServiceOrderInfo } from '@/apis/work-order-api'
import {timestampNumber2dateString} from "@/utils/date-util";
import {useSysDictDataStore} from "@/store/system-data";
import {useCustomerAccountStore, useCustomerStore, useCustomerStoreInfoStore} from "@/store/customer-data";
import {useProjectTaskDataStore} from "@/store/work-order-data";

// 定义 props
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  serviceOrderId: {
    type: [String, Number],
    default: null
  }
})

// 定义 emits
const emit = defineEmits(['update:modelValue'])

// 响应式数据
const visible = ref(false)
const loading = ref(false)
const serviceOrderData = ref(null)

// store
// 系统字典
const sysDictDataStore = useSysDictDataStore();
// 项目任务单表
const workOrderDataStore = useProjectTaskDataStore();
// 客户信息
const customerStore = useCustomerStore();
// 客户门店信息
const customerStoreInfoStore = useCustomerStoreInfoStore();
// 客户账号
const customerAccountStore = useCustomerAccountStore();

// 异常记录表格列定义
const exceptionTableColumns = ref([
  { title: 'ID', dataIndex: 'id', width: 80 },
  { title: '异常状态', dataIndex: 'exceptionStatus', width: 100, slotName: 'exceptionStatus' },
  { title: '异常开始时间', dataIndex: 'exceptionStartTime', width: 160, slotName: 'exceptionStartTime' },
  { title: '异常恢复时间', dataIndex: 'exceptionRecoveryTime', width: 160, slotName: 'exceptionRecoveryTime' },
  { title: '异常原因', dataIndex: 'exceptionReason', width: 200, ellipsis: true, tooltip: true },
  { title: '解决方案', dataIndex: 'solution', ellipsis: true, tooltip: true },
  { title: '备注', dataIndex: 'remark', ellipsis: true, tooltip: true }
])

// 监听 modelValue 变化
watch(() => props.modelValue, (newVal) => {
  visible.value = newVal
  if (newVal && props.serviceOrderId) {
    fetchServiceOrderData()
  }
})

// 监听 visible 变化
watch(visible, (newVal) => {
  emit('update:modelValue', newVal)
})

// 获取服务单详情数据
const fetchServiceOrderData = async () => {
  if (!props.serviceOrderId) {
    Message.warning('项目服务单ID不能为空')
    return
  }

  loading.value = true
  try {
    // TODO 当修改项目服务单时不想修改已经保存过的异常单，可以改用`getProjectServiceOrder`
    const response = await getProjectServiceOrderInfo(props.serviceOrderId)
    if (response.code === 0) {
      serviceOrderData.value = response.data
    } else {
      Message.error(response.message || '获取项目服务单详情失败')
      console.error('获取项目服务单详情失败:', response)
    }
  } catch (error) {
    Message.warning(error.message || '获取项目服务单详情失败')
    console.warn('获取项目服务单详情失败:', error)
  } finally {
    loading.value = false
  }
}

// 关闭弹框
const handleCancel = () => {
  visible.value = false
  serviceOrderData.value = null
}

// 格式化日期
const formatDate = (dateString) => {
  if (!dateString) return '—'
  // return dateString.substring(0, 16).replace('T', ' ');
  return timestampNumber2dateString(dateString)
}

// 格式化扩展字段
const formatExtensionFields = (extensionFields) => {
  if (!extensionFields) return '—'
  if (typeof extensionFields === 'string') {
    try {
      return JSON.stringify(JSON.parse(extensionFields), null, 2)
    } catch (e) {
      return extensionFields
    }
  }
  return JSON.stringify(extensionFields, null, 2)
}
// 项目任务单（项目单）-名称
const getProjectTaskText = (projectTaskId) => {
  const projectTask = workOrderDataStore.projectTaskDataIdMap.get(projectTaskId + "");
  return projectTask ? projectTask.projectName : projectTaskId;
};
// 客户-名称
const getCustomerText = (customerCode) => {
  // 数据库中叫客户编码 但数据同步过来实际上存的是客户ID 所以页面的条件查询、详情名称都是用ID来实现的
  const customer = customerStore.customerIdMap.get(customerCode + "");
  return customer ? customer.customerName : customerCode;
};

// 门店-名称
const getStoreText = (storeCode) => {
  const store = customerStoreInfoStore.customerStoreIdMap.get(storeCode + "");
  return store ? store.storeName : storeCode;
}



// 获取 服务人员 姓名
const getServiceStaffText = (serviceStaffCode) => {
  const customerAccount = customerAccountStore.customerAccountCodeMap.get(serviceStaffCode + "");
  return customerAccount ? customerAccount.username : serviceStaffCode;
}
// 获取服务状态文本
const getServiceStatusText = (status) => {
  const dictItem = sysDictDataStore.projectServiceStatusMap.get(status + "");
  return dictItem ? dictItem.dictLabel : '未知';
}

// 获取服务类型文本
const getServiceTypeText = (serviceType) => {
  const dictItem = sysDictDataStore.projectServiceTypeMap.get(serviceType + "");
  return dictItem ? dictItem.dictLabel : '未知';
}

// 获取服务频次文本
const getServiceFrequencyText = (serviceFrequencyType) => {
  const dictItem = sysDictDataStore.projectServiceFrequencyMap.get(serviceFrequencyType + "");
  return dictItem ? dictItem.dictLabel : '未知';
}

// 获取建议时间文本（根据服务频次匹配对应的数据字典）
const getSuggestedTimeText = (suggestedTime, serviceFrequency) => {
  if (!suggestedTime) return '—';
  
  // 根据服务频次选择对应的数据字典
  let dictMap;
  if (serviceFrequency === '2') {
    dictMap = sysDictDataStore.projectServiceSuggestedTimeMap_2;
  } else if (serviceFrequency === '3') {
    dictMap = sysDictDataStore.projectServiceSuggestedTimeMap_3;
  } else if (serviceFrequency === '4') {
    dictMap = sysDictDataStore.projectServiceSuggestedTimeMap_4;
  } else {
    // 如果服务频次不是2,3,4，直接返回原始值
    return suggestedTime;
  }
  
  const dictItem = dictMap?.get(suggestedTime + "");
  return dictItem ? dictItem.dictLabel : suggestedTime;
}

const getTaskCategoryText = (taskCategory) => {
  const dictItem = sysDictDataStore.projectServiceTaskCategoryMap.get(taskCategory + "");
  return dictItem ? dictItem.dictLabel : '未知';
}

// 获取是否强制协同文本
const getIsOvertimeText = (isOvertime) => {
  const dictItem = sysDictDataStore.projectServiceIsOvertimeMap.get(isOvertime + "");
  return dictItem ? dictItem.dictLabel : '未知';
}

// 获取服务状态颜色
const getServiceStatusColor = (status) => {
  // 颜色需要根据实际情况定义
  const map = {'1': 'blue', '2': 'orange', '3': 'green', '4': 'red'}
  return map[status] || 'gray'
}

// 获取异常状态颜色
const getExceptionStatusColor = (status) => {
  // 颜色需要根据实际情况定义
  const map = {1: 'orange', 2: 'red'}
  return map[status] || 'gray'
}

// 获取异常状态文本
const getExceptionStatusText = (status) => {
  const dictItem = sysDictDataStore.projectServiceExceptionStatusMap.get(status + "");
  return dictItem ? dictItem.dictLabel : '未知';
}
</script>

<style scoped>
.service-order-info-modal-content {
  padding: 16px 0;
}

.loading-container {
  text-align: center;
  padding: 50px 0;
}

.loading-container p {
  margin-top: 16px;
  color: #666;
}

.no-data-container {
  text-align: center;
  padding: 50px 0;
}

.service-order-info {
  max-height: 600px;
  overflow-y: auto;
}

:deep(.arco-descriptions-item-label) {
  font-weight: 500;
  color: #1890ff;
}

pre {
  font-family: 'Courier New', Consolas, monospace;
  font-size: 12px;
  background-color: #f5f5f5;
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  max-height: 150px;
  overflow-y: auto;
}
</style>